import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { api, type TripName, type TripView } from "./api.ts";
import { openFeedback } from "./feedback.ts";
import { tripDates, tripDatesShort } from "./format.ts";
import { navigate, tripPath } from "./router.ts";
import { Icon } from "./souvenir.ts";
import { ItemIcon } from "./ui.tsx";

// The trip's name is its menu (DESIGN.md, "The trip's name is its menu; Back stays inside the trip"):
// tapping the name and dates opens Your trips, the other trips by name (switching straight to one), Trip
// settings, and Send feedback (features/feedback.md). Leaving the trip is two deliberate taps through it,
// which says where each leads, or on a computer one on Tripmo's logo, a link to Your trips as on every page
// (coming back puts the trip where it was). A menu button as the platform's are: Enter, Space or the arrows
// open it on an item, the arrows move, Escape closes it (and nothing under it) with focus back on the name,
// a tap outside closes it.

export function TripMenu({
  trip,
  className,
  settings,
  onSettings,
}: {
  trip: TripView;
  // tm-trip-name on a computer's bar, tm-pill-name in a phone's pill.
  className: string;
  // Trip settings is open.
  settings: boolean;
  onSettings: () => void;
}) {
  const [open, setOpen] = useState<"first" | "last" | null>(null);
  // My other trips, as last heard: asked for once the page is up, and again each time the menu opens.
  const [names, setNames] = useState<TripName[] | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const dates = tripDates(trip.dates);

  const load = () =>
    api.tripNames().then(
      ({ trips }) => setNames(trips),
      () => {},
    );
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per trip page
  useEffect(() => {
    load();
  }, [trip.id]);

  const close = (refocus = true) => {
    setOpen(null);
    if (refocus) button.current?.focus();
  };
  const items = () => [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];

  // Opened: focus on its first item (or its last, opened with the up arrow). While open, its keys are its
  // own: Escape closes the menu only, not the card or view under it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: set up each time it opens
  useEffect(() => {
    if (!open) return;
    const list = items();
    (open === "last" ? list.at(-1) : list[0])?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        close();
      } else if (e.key === "Tab") {
        e.preventDefault();
        close();
      }
    };
    addEventListener("keydown", onKey, true);
    return () => removeEventListener("keydown", onKey, true);
  }, [open]);

  const onButtonKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      load();
      setOpen(e.key === "ArrowUp" ? "last" : "first");
    }
  };
  const onMenuKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      list[(i + list.length) % list.length]?.focus();
    };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(list.length - 1);
  };
  const choose = (run: () => void) => {
    close();
    run();
  };

  const others = (names ?? []).filter((t) => t.id !== trip.id && t.status === "member");
  return (
    <div className="tm-trip-menu-wrap">
      <button
        ref={button}
        type="button"
        className={`${className}${open ? " is-open" : ""}`}
        // Its name first, then its dates: what it says, in the order people look for it.
        aria-label={dates ? `${trip.title}, ${dates}` : trip.title}
        aria-haspopup="menu"
        aria-expanded={Boolean(open)}
        aria-controls={open ? menuId : undefined}
        onClick={() => {
          if (open) close();
          else {
            load();
            setOpen("first");
          }
        }}
        onKeyDown={onButtonKey}
      >
        {dates && <TripDates full={dates} short={tripDatesShort(trip.dates)} />}
        <span className="tm-trip-title">
          <span className="title">{trip.title}</span>
          <span className={`tm-trip-caret${open ? " is-up" : ""}`} aria-hidden="true">
            <Icon name="chevron" size={16} />
          </span>
        </span>
      </button>
      {open && (
        <>
          {/* A tap anywhere else closes it, and does nothing else. */}
          <div className="tm-menu-scrim" onPointerDown={() => close(false)} aria-hidden="true" />
          <div
            ref={menu}
            id={menuId}
            className="tm-trip-menu"
            role="menu"
            aria-label={trip.title}
            onKeyDown={onMenuKey}
          >
            <MenuItem icon={<Icon name="house" size={20} />} onClick={() => choose(() => navigate("/"))}>
              Your trips
            </MenuItem>
            {others.length > 0 && (
              // biome-ignore lint/a11y/useSemanticElements: a group of a menu's items (ARIA menu), not of form fields
              <div role="group" aria-label="Other trips" className="tm-menu-group">
                <div className="eyebrow tm-muted tm-menu-label" aria-hidden="true">
                  Other trips
                </div>
                {others.map((t) => (
                  <MenuItem
                    key={t.id}
                    icon={<Icon name="map" size={20} />}
                    note={tripDates(t.dates)}
                    onClick={() => choose(() => navigate(tripPath(t.id)))}
                  >
                    {t.title}
                  </MenuItem>
                ))}
              </div>
            )}
            <hr className="tm-menu-separator" />
            <MenuItem
              icon={<ItemIcon name="gear" size={20} />}
              current={settings}
              onClick={() => choose(() => (settings ? undefined : onSettings()))}
            >
              Trip settings
            </MenuItem>
            <MenuItem
              icon={<ItemIcon name="feedback" size={20} />}
              onClick={() => choose(() => openFeedback({ kind: "bug" }))}
            >
              Send feedback
            </MenuItem>
          </div>
        </>
      )}
    </div>
  );
}

export function MenuItem({
  icon,
  note,
  current = false,
  onClick,
  children,
}: {
  icon: ReactNode;
  note?: string | null;
  current?: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      className={`tm-menu-item${current ? " is-current" : ""}`}
      aria-current={current ? "page" : undefined}
      onClick={onClick}
    >
      <span className="tm-menu-icon">{icon}</span>
      <span className="tm-menu-text">
        <span className={`callout tm-menu-name${current ? " is-current" : ""}`}>{children}</span>
        {note && <span className="caption tm-muted">{note}</span>}
      </span>
    </button>
  );
}

// The trip's dates, on one line from a 320 px phone up (DESIGN.md, "The trip's name is its menu"): in full
// where they fit, else with the year said once, at the end ("28 Nov – 2 Jan 2027"), never "28 Nov 2026 –"
// over "2 Jan 2027". Both are laid out in the same place, the full one hidden while it doesn't fit, so the
// line is as wide as the full dates where there's room for them and the choice follows the width (a turned
// phone, the font arriving) without a frame of the other; past even the shorter ones (a large text size),
// an ellipsis.
export function TripDates({ full, short }: { full: string; short: string | null }) {
  const line = useRef<HTMLSpanElement>(null);
  const whole = useRef<HTMLSpanElement>(null);
  const [fits, setFits] = useState(true);
  useLayoutEffect(() => {
    const box = line.current;
    const text = whole.current;
    if (!short || !box || !text) return;
    const measure = () => setFits(text.scrollWidth <= box.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(text);
    return () => observer.disconnect();
  }, [short]);
  // Nothing shorter to say (a trip of over a year, "28 Nov 2026 – 2 Jan 2028"): both years stay, breaking
  // between the dates rather than losing one to an ellipsis.
  if (!short) {
    const [from, to] = full.split(" – ");
    return (
      <span className="eyebrow tm-sun-text">
        <span className="tm-nowrap">{from}</span>
        {to && (
          <>
            {" – "}
            <span className="tm-nowrap">{to}</span>
          </>
        )}
      </span>
    );
  }
  return (
    <span ref={line} className="eyebrow tm-sun-text tm-trip-dates">
      <span ref={whole} className={fits ? undefined : "is-hidden"}>
        {full}
      </span>
      {!fits && <span>{short}</span>}
    </span>
  );
}
